import React, { useContext, useEffect, useMemo } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { InformationMessage } from "nemuru-components";
import IconTitle from "../../../shared/components/IconTitle";
import Text from "../../../shared/components/Text";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import {
  ID_DOCUMENT_VERIFICATION_EXCEPTIONS,
  ID_DOCUMENT_VERIFICATION_STATUSES,
  getDocumentIdDocumentLabel,
  idDocumentVerificationExceptionMatches,
} from "../config";
import MainButton from "../../../shared/components/MainButton";
import { RedirectToMerchantButton } from "../../../shared/components/RedirectToMerchantButton";

export const ValidationResult = ({ onSubmitForm }) => {
  const { palette } = useTheme();
  const {
    state: { idDocumentVerification, participant },
    dispatch,
  } = useContext(ProcessContext);

  const onboardingDetailsId = participant.onboardingDetails?.id;
  const documentType = idDocumentVerification.documentType;

  const restartLoading =
    !!idDocumentVerification.restartingToProcessInformation &&
    !!idDocumentVerification.loading;

  const verificationStatus = useMemo(() => {
    return (
      idDocumentVerification.lastVerifyStatus ??
      idDocumentVerification.status ??
      ID_DOCUMENT_VERIFICATION_STATUSES.NULL
    );
  }, [idDocumentVerification]);

  const failedAttempts = idDocumentVerification.failedAttempts ?? 0;
  const maxFailedAttempts = idDocumentVerification.maxFailedAttempts ?? 3;
  const isRetryAllowed =
    verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.FAILED &&
    failedAttempts < maxFailedAttempts;
  const exception = idDocumentVerification.error?.payload?.data?.exception;
  const failureReasonTextId = idDocumentVerificationExceptionMatches(
    exception,
    ID_DOCUMENT_VERIFICATION_EXCEPTIONS.PROCESS_EXPIRED
  )
    ? "ID_DOC_VERIFICATION_REASON_TIMEOUT"
    : "ID_DOC_VERIFICATION_REASON_RECOGNITION";

  useEffect(() => {
    if (verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED) {
      onSubmitForm({});
    }
  }, [verificationStatus, onSubmitForm]);

  if (
    verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED ||
    verificationStatus !== ID_DOCUMENT_VERIFICATION_STATUSES.FAILED
  )
    return null;

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          title={
            <Text
              tId="DOCUMENT_ID_VERIFICATION"
              dynamicDataTexts={[getDocumentIdDocumentLabel(documentType)]}
            />
          }
          subtitle={<Text tId="VERIFICATION_FAILED" />}
        />
      </Box>
      <Box>
        <Typography
          variant="subtitle1"
          align="center"
          style={{ color: palette.text.title }}
        >
          <Text strong tId="ID_DOC_VERIFICATION_FAILED_TITLE" />
        </Typography>
        <Box mt={2}>
          <Typography align="center" variant="body2" color="textSecondary">
            <Text
              tId={
                isRetryAllowed
                  ? failureReasonTextId
                  : "ID_DOC_VERIFICATION_MAX_ATTEMPTS"
              }
            />
          </Typography>
        </Box>
      </Box>
      <Box>
        {isRetryAllowed ? (
          <InformationMessage
            variant={{
              type: "warning",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  warning
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: <Text tId="ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_TITLE" />,
              body: (
                <Text
                  tId="ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_BODY"
                  dynamicDataTexts={[failedAttempts, maxFailedAttempts]}
                />
              ),
            }}
          />
        ) : null}
      </Box>
      <Box>
        {isRetryAllowed ? (
          <MainButton
            disabled={restartLoading}
            onClick={() => {
              dispatch({
                type: "RESTART_ID_DOCUMENT_VERIFICATION_FROM_VALIDATION",
              });
              dispatch({
                type: "REQUEST_POST_ID_DOCUMENT_VERIFICATION_START",
                payload: {
                  onboarding_details_id: onboardingDetailsId,
                  document_type: documentType,
                },
              });
            }}
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gridGap={8}
            >
              <Text tId="RESTART_PROCESS" />
              {restartLoading && <CircularProgress size={20} color="inherit" />}
            </Box>
          </MainButton>
        ) : (
          <RedirectToMerchantButton />
        )}
      </Box>
    </>
  );
};
